import { Link, useLocation, Navigate } from "react-router-dom";
import { MapPin, Phone, Mail, Check, Clock, Car, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { THERAPIST, SERVICES } from "@/data/catalog";
import { CITY_BY_SLUG } from "@/data/cities";
import ReserveButton from "@/components/ReserveButton";
import useSEO from "@/hooks/useSEO";

/**
 * Dynamic city SEO page — one route per city, unique content per city.
 * Modes:
 *   home    → service à domicile réel (Laurentides)
 *   clinic  → SEO uniquement, ramène en clinique Blainville
 *   future  → ouverture prévue (Estrie automne 2027)
 */
export default function CityPage() {
    const { pathname } = useLocation();
    const slug = pathname.replace(/^\/+/, "").split("/")[0];
    const city = CITY_BY_SLUG[slug];

    const canonical = city ? `https://massoclic.ca/${city.slug}` : "https://massoclic.ca/";
    const businessType = city?.mode === "future" ? "Organization" : "HealthAndBeautyBusiness";

    const jsonLd = city ? {
        "@context": "https://schema.org",
        "@type": businessType,
        "@id": `${canonical}#business`,
        name: `Jérémy Tessier — Massothérapie${city.mode === "home" ? " (service " + city.city + ")" : ""}`,
        description: city.description,
        url: canonical,
        image: "https://massoclic.ca/icon.svg",
        telephone: THERAPIST.phone,
        email: THERAPIST.email,
        priceRange: "$$",
        address: {
            "@type": "PostalAddress",
            streetAddress: "1436 boulevard Curé-Labelle",
            addressLocality: "Blainville",
            addressRegion: "QC",
            postalCode: "J7C 2P4",
            addressCountry: "CA",
        },
        geo: { "@type": "GeoCoordinates", latitude: 45.6742, longitude: -73.8837 },
        areaServed: [
            { "@type": "City", name: city.city, containedInPlace: { "@type": "AdministrativeArea", name: city.region } },
        ],
        makesOffer: SERVICES.map((s) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: s.name, description: s.description },
        })),
        sameAs: ["https://massoclic.ca"],
    } : undefined;

    useSEO({
        title: city?.title,
        description: city?.description,
        canonical,
        ogImage: "https://massoclic.ca/icon.svg",
        jsonLd,
    });

    if (!city) return <Navigate to="/" replace />;

    const isFuture = city.mode === "future";
    const isHome = city.mode === "home";

    return (
        <main data-testid={`city-page-${city.slug}`} className="overflow-hidden">
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-14">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-[#3D8C95]/10 blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#E07A5F]/10 blur-3xl" />
                </div>
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <MapPin size={14} className="text-[#E07A5F]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            {city.region}
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        Massothérapie{" "}
                        <span className="italic text-[#E07A5F]">à {city.city}</span>
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed max-w-3xl">
                        {city.heroLead}
                    </p>

                    {isFuture ? (
                        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E07A5F]/10 border border-[#E07A5F]/30">
                            <CalendarDays size={14} className="text-[#E07A5F]" />
                            <span className="text-xs uppercase tracking-widest text-[#E07A5F] font-medium">
                                Ouverture prévue · {city.futureDate}
                            </span>
                        </div>
                    ) : (
                        <div className="mt-6 flex flex-wrap gap-3 items-center">
                            <ReserveButton
                                mailSubject={`Réservation — Massage ${city.city}`}
                                mailBody={`Bonjour Jérémy,\n\nJe souhaite réserver un massage${isHome ? " à domicile" : ""} à ${city.city}.\n\n• Soin souhaité : \n• Date envisagée : \n• Adresse (si à domicile) : \n\nMerci !`}
                                quoteSubject={`Devis — ${city.city}`}
                                quoteBody={`Bonjour Jérémy,\n\nJe souhaite un devis pour un service à ${city.city}.\n\n• Type de service (entreprise, événement, groupe, autre) : \n• Date : \n• Nombre de participants : \n\nMerci !`}
                            />
                            <a href={`tel:${THERAPIST.phone}`}>
                                <Button
                                    variant="outline"
                                    className="rounded-full border-[#3D8C95] text-[#3D8C95] hover:bg-[#3D8C95] hover:text-white px-8 py-6"
                                >
                                    <Phone size={16} className="mr-2" />
                                    {THERAPIST.phone}
                                </Button>
                            </a>
                        </div>
                    )}
                </div>
            </section>

            {/* Unique content */}
            <section className="py-12 md:py-16 bg-white">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#3D8C95] mb-4">
                        {isHome ? "Service à domicile" : isFuture ? "À venir" : "Cabinet accessible"}
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29] leading-tight mb-6">
                        {isHome
                            ? `Un massothérapeute qui se déplace à ${city.city}`
                            : isFuture
                              ? `Bientôt à ${city.city}`
                              : `Massothérapie accessible depuis ${city.city}`}
                    </h2>
                    {city.paragraphs.map((p, i) => (
                        <p key={i} className="mt-4 text-[#3A3735] leading-relaxed text-base md:text-lg">
                            {p}
                        </p>
                    ))}
                </div>
            </section>

            {/* Practical info */}
            {!isFuture && (
                <section className="py-14 md:py-20 bg-[#FAF7F2]">
                    <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
                        <div className="rounded-3xl p-6 bg-white border border-[#E8E2D9]">
                            <MapPin size={24} className="text-[#E07A5F] mb-3" />
                            <h3 className="font-display text-xl text-[#2C2A29]">Cabinet</h3>
                            <p className="mt-2 text-sm text-[#6E6862]">
                                1436 boulevard Curé-Labelle<br />Blainville, QC J7C 2P4
                            </p>
                            <p className="mt-2 text-xs text-[#3D8C95]">{city.distanceKm} km de {city.city}</p>
                        </div>
                        <div className="rounded-3xl p-6 bg-white border border-[#E8E2D9]">
                            <Clock size={24} className="text-[#3D8C95] mb-3" />
                            <h3 className="font-display text-xl text-[#2C2A29]">Horaires</h3>
                            <p className="mt-2 text-sm text-[#6E6862]">
                                Lundi au samedi<br />9 h — 20 h
                            </p>
                        </div>
                        <div className="rounded-3xl p-6 bg-white border border-[#E8E2D9]">
                            <Car size={24} className="text-[#C44569] mb-3" />
                            <h3 className="font-display text-xl text-[#2C2A29]">Accès</h3>
                            <p className="mt-2 text-sm text-[#6E6862]">
                                {isHome ? "Service à domicile" : "Autoroutes 15 & 640"}
                                <br />
                                {isHome ? `Rayon 40 km · +${20}$ à l'unité` : "Stationnement gratuit"}
                            </p>
                        </div>
                    </div>
                </section>
            )}

            {/* Related services quick nav */}
            {!isFuture && (
                <section className="py-14 md:py-20 bg-white">
                    <div className="max-w-5xl mx-auto px-6 md:px-12">
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29] mb-8">
                            Les soins offerts
                        </h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {SERVICES.map((s) => (
                                <Link
                                    to="/menu"
                                    key={s.id}
                                    data-testid={`city-svc-${s.id}`}
                                    className="rounded-2xl p-5 border border-[#E8E2D9] hover:border-[#E07A5F] transition-colors"
                                >
                                    <div className="font-display text-lg text-[#2C2A29]">{s.name}</div>
                                    <div className="text-xs text-[#6E6862] mt-0.5">{s.subtitle}</div>
                                    <div className="text-sm text-[#E07A5F] font-medium mt-3">
                                        {s.variants.map((v, i) => (
                                            <span key={v.id}>
                                                {i > 0 && <span className="text-[#6E6862] mx-2">·</span>}
                                                {v.label} {v.price} $
                                            </span>
                                        ))}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="py-16 md:py-24 text-center">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <h2 className="font-display text-3xl md:text-5xl text-[#2C2A29] leading-tight">
                        {isFuture
                            ? "Envie d'être informé du lancement ?"
                            : `Prêt à réserver votre massage à ${city.city} ?`}
                    </h2>
                    <p className="mt-4 text-[#6E6862] leading-relaxed">
                        {isFuture
                            ? "Écrivez-moi pour être inscrit en liste privilégiée dès l'ouverture."
                            : "Confirmation par courriel. Reçu d'assurance fourni pour chaque séance."}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        {!isFuture && (
                            <ReserveButton
                                mailSubject={`Réservation — Massage ${city.city}`}
                                mailBody={`Bonjour Jérémy,\n\nJe souhaite réserver un massage${isHome ? " à domicile" : ""} à ${city.city}.\n\n• Soin souhaité : \n• Date envisagée : \n• Adresse (si à domicile) : \n\nMerci !`}
                                quoteSubject={`Devis — ${city.city}`}
                                quoteBody={`Bonjour Jérémy,\n\nJe souhaite un devis pour un service à ${city.city}.\n\n• Type de service : \n• Date : \n• Nombre de participants : \n\nMerci !`}
                            />
                        )}
                        {isFuture && (
                            <a href={`mailto:${THERAPIST.email}?subject=${encodeURIComponent(`Préinscription ${city.city} — ${city.futureDate}`)}&body=${encodeURIComponent(`Bonjour Jérémy,\n\nJe souhaite être informé(e) de l'ouverture prévue en ${city.futureDate} à ${city.city}.\n\nMerci !`)}`}>
                                <Button className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6 text-base">
                                    <Mail size={16} className="mr-2" />
                                    Me préinscrire
                                </Button>
                            </a>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
}
